import { Avatar, CardMedia, Stack, Typography } from '@mui/material'

import {
  ArtistDmRoomDetailQuery,
  DmRoomMessageBoxFragment,
} from '@/clients/utoniq-core/schema'
import { DmRoomMessageType } from '@/clients/utoniq-core/schema'
import { Cell, CellFrame } from '@/core/architecture'
import { Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'

/**
 * sendUserId に合致する送信者（アーティストまたはユーザー）を取得
 */
const getSender = (
  dmRoom: ArtistDmRoomDetailQuery['dmRoom'],
  sendUserId: string
) => {
  if (dmRoom?.artist?.id === sendUserId) {
    return dmRoom.artist
  }
  return dmRoom?.users?.find(user => user.id === sendUserId)
}

type DmRoomMessageProps = {
  userId: string
  dmRoom: ArtistDmRoomDetailQuery['dmRoom']
  dmRoomMessage: DmRoomMessageBoxFragment
}
export const DmRoomMessage: Organism<DmRoomMessageProps> = ({
  userId,
  dmRoom,
  dmRoomMessage,
}) => {
  const sender = getSender(dmRoom, dmRoomMessage.sendUserId)
  const isSent = dmRoomMessage.sendUserId === userId

  return (
    <CellFrame>
      <Stack
        direction={isSent ? 'row-reverse' : 'row'}
        spacing={2}
        alignItems="flex-start"
      >
        <Avatar
          variant="circular"
          sizes="medium"
          src={sender?.avatar?.smallUri ?? ''}
        />
        <Stack
          spacing={1}
          sx={{
            flexGrow: 1,
            alignItems: isSent ? 'flex-end' : 'flex-start',
            width: '100%',
          }}
        >
          <Stack
            direction={isSent ? 'row' : 'row-reverse'}
            spacing={2}
            alignItems="baseline"
          >
            <Typography variant="caption" color="text.hint">
              {/**
               * useSubscription経由で取得したメッセージでは
               * @clientフィールドが正しく計算されないため、
               * 直接DateTime.formatRelativeSecondAdjustを呼び出す
               */}
              {DateTime.formatRelativeSecondAdjust(dmRoomMessage.createdAt)}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 'bold' }}
            >
              {sender?.displayName ?? ''}
            </Typography>
          </Stack>
          {dmRoomMessage.type === DmRoomMessageType.Text && (
            <DmRoomTextMessage message={dmRoomMessage.message} />
          )}
          {dmRoomMessage.type === DmRoomMessageType.Image && (
            <DmRoomImageMessage
              uri={dmRoomMessage.resources?.[0]?.mediumUri ?? ''}
            />
          )}
        </Stack>
      </Stack>
    </CellFrame>
  )
}

type DmRoomTextMessageProps = {
  message: DmRoomMessageBoxFragment['message']
}
/** テキストメッセージ表示 */
const DmRoomTextMessage: Cell<DmRoomTextMessageProps> = ({ message }) => {
  return (
    <Stack
      bgcolor={'grey.50'}
      p={2}
      borderRadius={1}
      sx={{
        maxWidth: '60%',
        wordBreak: 'break-all',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  )
}

type DmRoomImageMessageProps = {
  uri: string
}
/** 画像メッセージ表示 */
const DmRoomImageMessage: Cell<DmRoomImageMessageProps> = ({ uri }) => {
  return (
    <Stack borderRadius={1} sx={{ maxWidth: '60%' }}>
      <CardMedia component="img" image={uri} alt="image" />
    </Stack>
  )
}
